import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { RegmembersService } from "src/app/services/regmembers.service";
import { PaymentsService } from "src/app/services/payments.service";
import { RegmemCoursesService } from "src/app/services/regmem-courses.service";
import { CoursesService } from "src/app/services/courses.service";
import { Payment } from "src/app/models/payment";
import { User } from "src/app/models/user.model";
import { MemberCourse } from "src/app/models/member-course.model";
import { Course } from "src/app/models/course.model";
import { Table } from "primeng/table";


interface PaymentFilter {
  email: string;
  paymentType: string;
  paymentId: number;    
  courseCode: string;
  amount: number;
  paymentDate: Date;
  paymentNote: string;
  stripePaymentIntentId: string;
  stripeCheckoutSessionId: string;
}

@Component({
  selector: "app-member-course-payments",
  templateUrl: "./member-course-payments.component.html",
  styleUrls: ["./member-course-payments.component.css"],
})
export class MemberCoursePaymentsComponent implements OnInit {
  constructor(
    private regmembersService: RegmembersService,
    private paymentsService: PaymentsService,
    private regmemCoursesService: RegmemCoursesService,
    private coursesService: CoursesService,
    private activatedRoute: ActivatedRoute
  ) { }

  first = 0;
  rows = 15;
  sortField: string;
  sortOrder: number;
  totalRecords = 0;

  memberName: string;
  member: User = {} as User;
  memberPayments: Payment[] = [];
  memberCourses: MemberCourse[] = [];
  courses: Course[] = [];
  memberCourseOptions: any[] = [];
  courseCodeOptions: any[] = [];
  paymentTypeOptions: string[] = ['retail', 'caola', 'edison', 'csiu', 'other'];
  paymentTypeDropdownOptions: { label: string; value: string }[] = this.paymentTypeOptions.map(t => ({ label: t, value: t }));

  cols: any[] = [];
  loading = true;
  @ViewChild('dt') table: Table;

  displayDialog: boolean = false;
  isEditMode: boolean = false;
  newPayment: any = {} as Payment & { courseId: number };
  errorMessage: string;
  successMessage: string;

  async ngOnInit() {
    this.cols = [
      { field: "paymentType", header: "Payment Type" },
      { field: "courseCode", header: "Course Code" },
      { field: "amount", header: "Amount" },
      { field: "email", header: "Email" },
      { field: "paymentDate", header: "Payment Date" },
      { field: "paymentNote", header: "Payment Note" },
      { field: "memberCourse", header: "Member Course" },
      { field: "stripePaymentIntentId", header: "Stripe Payment Intent ID" },
      { field: "stripeCheckoutSessionId", header: "Stripe Checkout Session ID" },
      { field: "actions", header: "Actions" },
    ];

    this.activatedRoute.params.subscribe(async (params) => {
      this.memberName = params["membername"];
      await this.loadMemberPayments();
    });
  }

  async loadMemberPayments() {
    try {
      this.member = await this.regmembersService.getUserBy(this.memberName);
      const filters = { email: this.member.eaddress } as PaymentFilter;
      const response = await this.paymentsService.getMemberPayments(filters);
      // Ensure response is always an array
      this.memberPayments = Array.isArray(response) ? response : [];
      this.totalRecords = this.memberPayments.length;
      
      // Load member courses and courses for display
      await this.loadMemberCourses();
      
      console.log('Loaded payments:', this.memberPayments);
      console.log('Total records:', this.totalRecords);
      console.log('Member eaddress:', this.member.eaddress);
    } catch (error) {
      console.error('Error loading payments:', error);
      this.memberPayments = [];
      this.totalRecords = 0;
    } finally {
      this.loading = false;
    }
  }

  async loadMemberCourses() {
    try {
      this.memberCourses = await this.regmemCoursesService.getMemberCourses(this.memberName);
      this.courses = await this.coursesService.findAll();
      
      // Create dropdown options for member course (value = member course id)
      this.memberCourseOptions = this.memberCourses.map(mc => {
        const course = this.courses.find(c => c.id === mc.courseId);
        return {
          label: course ? `${course.courseName} (${mc.status})` : `Course ${mc.courseId} (${mc.status})`,
          value: mc.id,
          memberCourse: mc
        };
      });

      // Create course code options for dropdown
      this.courseCodeOptions = this.courses.map(course => ({
        label: `${course.courseCode} - ${course.courseName}`,
        value: course.courseCode
      }));
    } catch (error) {
      console.error('Error loading member courses:', error);
      this.memberCourses = [];
      this.memberCourseOptions = [];
      this.courseCodeOptions = [];
    }
  }

  getMemberCourseForPayment(payment: Payment): MemberCourse | null {
    // MemberCourse has paymentId property, so find member course by matching paymentId
    const found = this.memberCourses.find(mc => mc.paymentId === payment.paymentId);
    if (found) {
      console.log('Found member course for payment:', payment.paymentId, found);
    } else {
      console.log('No member course found for payment:', payment.paymentId);
    }
    return found || null;
  }

  getMemberCourseDisplayName(memberCourse: MemberCourse | null): string {
    if (!memberCourse) return '';
    const course = this.courses.find(c => c.id === memberCourse.courseId);
    return course ? `${course.courseCode} (${memberCourse.status})` : `Course ${memberCourse.courseId} (${memberCourse.status})`;
  }

  async associatePaymentWithMemberCourse(payment: Payment, memberCourseId: number) {
    try {
      // Update payment with member course association
      // Assuming API endpoint exists - adjust as needed
      await this.paymentsService.updatePaymentMemberCourse(payment.paymentId, memberCourseId);
      
      // Update local data
      const paymentAny = payment as any;
      paymentAny.memberCourseId = memberCourseId;
      
      // Reload to get updated data
      await this.loadMemberPayments();
    } catch (error) {
      console.error('Error associating payment with member course:', error);
      alert('Failed to associate payment with member course');
    }
  }

  showDialogToAdd() {
    // Load courses if not already loaded
    if (this.courses.length === 0) {
      this.coursesService.findAll().then(courses => {
        this.courses = courses;
        this.courseCodeOptions = this.courses.map(course => ({
          label: `${course.courseCode} - ${course.courseName}`,
          value: course.courseCode
        }));
      });
    }

    this.newPayment = {
      paymentId: null,
      paymentType: '',
      email: this.member.eaddress || '',
      firstName: this.member.fname || null,
      lastName: this.member.lname || null,
      licenseInstate: this.member.licenseinstate || null,
      amount: 0,
      courseCode: '',
      stripePaymentIntentId: null,
      stripeCheckoutSessionId: null,
      paymentDate: new Date(),
      paymentNote: null,
      createDate: new Date(),
      modifiedDate: new Date(),
      memberCourseId: null
    };
    this.isEditMode = false;
    this.displayDialog = true;
    this.errorMessage = null;
    this.successMessage = null;
  }

  showDialogToEdit(payment: Payment) {
    const memberCourse = this.getMemberCourseForPayment(payment);
    this.newPayment = {
      ...payment,
      paymentDate: payment.paymentDate ? new Date(payment.paymentDate) : new Date(),
      memberCourseId: memberCourse?.id ?? null
    };
    this.setCourseIdFromCourseCode();
    this.isEditMode = true;
    this.displayDialog = true;
    this.errorMessage = null;
    this.successMessage = null;
  }

  /** Set newPayment.courseId from the current courseCode (lookup in courses). */
  onCourseCodeChange(courseCode: string) {
    this.setCourseIdFromCourseCode();
  }

  private setCourseIdFromCourseCode() {
    const code = this.newPayment?.courseCode;
    const course = this.courses.find(c => c.courseCode === code);
    (this.newPayment as any).courseId = course?.id;
  }

  getDateString(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onPaymentDateChange(value: string) {
    if (value) {
      this.newPayment.paymentDate = new Date(value);
    } else {
      this.newPayment.paymentDate = new Date();
    }
  }

  /** Build a Payment object with only the fields defined on the Payment interface. */
  private toPaymentPayload(src: any): Payment {
    return {
      paymentId: src.paymentId,
      paymentType: src.paymentType,
      email: src.email,
      firstName: src.firstName ?? null,
      lastName: src.lastName ?? null,
      licenseInstate: src.licenseInstate ?? null,
      amount: src.amount,
      courseCode: src.courseCode,
      stripePaymentIntentId: src.stripePaymentIntentId ?? null,
      stripeCheckoutSessionId: src.stripeCheckoutSessionId ?? null,
      paymentDate: src.paymentDate ? new Date(src.paymentDate) : new Date(),
      paymentNote: src.paymentNote ?? null,
      createDate: src.createDate ? new Date(src.createDate) : new Date(),
      modifiedDate: src.modifiedDate ? new Date(src.modifiedDate) : new Date(),
    };
  }

  async save() {
    try {
      this.errorMessage = null;
      this.successMessage = null;

      const payment = this.toPaymentPayload(this.newPayment);

      if (this.isEditMode && this.newPayment.paymentId != null) {
        await this.paymentsService.updatePayment(this.newPayment.courseId, this.member.memberid, this.newPayment.paymentId, payment);
        this.successMessage = 'Payment updated successfully';
      } else {
        await this.paymentsService.createPayment(this.newPayment.courseId, this.member.memberid, payment);
        this.successMessage = 'Payment added successfully';
      }

      this.displayDialog = false;
      await this.loadMemberPayments();
    } catch (error) {
      console.error('Error saving payment:', error);
      this.errorMessage = error.message || 'Failed to save payment';
    }
  }
}
